import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  RadioGroup,
  Radio,
  FormControlLabel,
  TextField,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloudDoneOutlinedIcon from '@mui/icons-material/CloudDoneOutlined';
import CloudSyncOutlinedIcon from '@mui/icons-material/CloudSyncOutlined';
import CloudOffOutlinedIcon from '@mui/icons-material/CloudOffOutlined';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import proxy from '../api/axios';
import { formatScientificText } from '../common/formatScientificText';

const ACCENT = '#137274';
const PINK = '#f50057';
const REVIEW = '#7c3aed';
const LEFT_PANEL_MIN = 200;
const LEFT_PANEL_MAX = 440;
const LEFT_PANEL_DEFAULT = 260;
const LEFT_PANEL_COLLAPSED = 44;
const AUTOSAVE_DEBOUNCE_MS = 1200;
const SUBMIT_RETRY_MS = 15000;

const pad = (n) => String(Math.max(0, n)).padStart(2, '0');
const formatTime = (seconds) => {
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${pad(h)}:${pad(m)}:${pad(s)}`;
  return `${pad(m)}:${pad(s)}`;
};

const answersToPayload = (answers) =>
  Object.entries(answers).map(([questionId, value]) => ({ questionId, ...value }));

const savedAnswersToState = (savedAnswers) => {
  const initial = {};
  (savedAnswers || []).forEach((a) => {
    const entry = {};
    if (a.selectedOptionIndex !== undefined && a.selectedOptionIndex !== null) {
      entry.selectedOptionIndex = a.selectedOptionIndex;
    } else if (a.answerText != null) {
      entry.answerText = a.answerText;
    }
    if (a.markedForReview) entry.markedForReview = true;
    if (Object.keys(entry).length) initial[a.questionId] = entry;
  });
  return initial;
};

const isAnswerFilled = (entry) =>
  Boolean(entry && (entry.selectedOptionIndex !== undefined || entry.answerText !== undefined));

const SAVE_STATUS_META = {
  saving: { label: 'Saving…', icon: CloudSyncOutlinedIcon, color: '#d97706' },
  saved: { label: 'Saved', icon: CloudDoneOutlinedIcon, color: '#1a7f37' },
  offline: { label: "Offline — will retry", icon: CloudOffOutlinedIcon, color: '#cf222e' },
};

const NeetTestLive = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const border = isDark ? '#30363d' : 'rgba(15, 60, 70, 0.12)';
  const surface = isDark ? '#161b22' : '#ffffff';
  const pageBg = isDark ? '#0f1419' : '#f4f8f9';
  const muted = isDark ? '#8b949e' : '#5f6b73';

  const [test, setTest] = useState(null);
  const [endsAt, setEndsAt] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({}); // questionId -> { selectedOptionIndex } | { answerText }
  const [isLoading, setIsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [finalized, setFinalized] = useState(false);
  const [confirmSubmitDialog, setConfirmSubmitDialog] = useState(false);
  const [timeoutDialog, setTimeoutDialog] = useState(false);
  const [submitPending, setSubmitPending] = useState(false); // time's up, but submit hasn't landed (offline)
  const [saveStatus, setSaveStatus] = useState(null); // null | 'saving' | 'saved' | 'offline'
  const [isFullscreen, setIsFullscreen] = useState(Boolean(document.fullscreenElement));
  const [activeSubject, setActiveSubject] = useState(null);
  const [leftPanelWidth, setLeftPanelWidth] = useState(LEFT_PANEL_DEFAULT);
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);
  const bodyRef = useRef(null);
  const isDraggingRef = useRef(false);

  const autoSubmitLock = useRef(false);
  const answersRef = useRef({});
  const skipNextAutosave = useRef(true); // don't autosave the answers we just loaded from the server

  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  useEffect(() => {
    let cancelled = false;
    const fetchTest = async () => {
      try {
        setIsLoading(true);
        const { data } = await proxy.get(`/neet-tests/${testId}/attempt`);
        if (cancelled) return;
        setTest(data);
        setAnswers(savedAnswersToState(data.savedAnswers));
        setActiveSubject((data.questions || []).find((q) => q.subject)?.subject || null);
        skipNextAutosave.current = true;
        const endMs = new Date(data.endsAt).getTime();
        setEndsAt(endMs);
        setTimeRemaining(Math.max(0, Math.floor((endMs - Date.now()) / 1000)));
      } catch (error) {
        if (cancelled) return;
        const code = error.response?.data?.code;
        const message = error.response?.data?.message || 'Failed to load test';
        if (code === 'ALREADY_SUBMITTED') {
          toast.info(message);
          navigate(`/neet-tests/${testId}/result`);
        } else if (code === 'TIME_OVER') {
          // The server already auto-finalizes an expired-but-unsubmitted attempt
          // (scored from its last autosaved answers) as part of this same
          // failed request — nothing left to do here but go see the result.
          toast.info('Your test time was already up — it was auto-submitted.');
          navigate(`/neet-tests/${testId}/result`);
        } else {
          toast.error(message);
          navigate('/neet-tests');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchTest();
    return () => {
      cancelled = true;
    };
  }, [testId, navigate]);

  // Autosave — periodically persists in-progress answers with no scoring, so a
  // reload or dropped connection before final submit doesn't lose anything
  // (mirrors how Contest commits each solved problem as you go).
  const saveAnswers = useCallback(async () => {
    if (finalized) return;
    setSaveStatus('saving');
    try {
      await proxy.put(`/neet-tests/${testId}/answers`, { answers: answersToPayload(answersRef.current) });
      setSaveStatus('saved');
    } catch (error) {
      setSaveStatus('offline');
    }
  }, [testId, finalized]);

  useEffect(() => {
    if (finalized) return undefined;
    if (skipNextAutosave.current) {
      skipNextAutosave.current = false;
      return undefined;
    }
    const timer = setTimeout(saveAnswers, AUTOSAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [answers, finalized, saveAnswers]);

  const finalizeTest = useCallback(
    async ({ reason }) => {
      if (autoSubmitLock.current || finalized) return;
      autoSubmitLock.current = true;
      try {
        await proxy.put(`/neet-tests/${testId}/submit`, {
          answers: answersToPayload(answersRef.current),
          autoSubmitted: reason === 'timeout',
        });
        setFinalized(true);
        setSubmitPending(false);
        if (reason === 'timeout') {
          setTimeoutDialog(true);
        } else {
          toast.success('Test submitted');
          navigate(`/neet-tests/${testId}/result`);
        }
      } catch (error) {
        autoSubmitLock.current = false;
        if (reason === 'timeout') {
          // Don't claim success — answers are already autosaved, so it's safe to
          // keep retrying in the background instead of lying about submission.
          setSubmitPending(true);
        } else {
          toast.error(error.response?.data?.message || 'Could not submit test');
        }
      }
    },
    [testId, finalized, navigate],
  );

  // Retry autosave / pending submit once connectivity returns, and poll as a
  // fallback in case the browser doesn't fire a reliable 'online' event.
  useEffect(() => {
    const retry = () => {
      if (submitPending) finalizeTest({ reason: 'timeout' });
      else if (saveStatus === 'offline') saveAnswers();
    };
    window.addEventListener('online', retry);
    return () => window.removeEventListener('online', retry);
  }, [submitPending, saveStatus, finalizeTest, saveAnswers]);

  useEffect(() => {
    if (!submitPending) return undefined;
    const id = setInterval(() => finalizeTest({ reason: 'timeout' }), SUBMIT_RETRY_MS);
    return () => clearInterval(id);
  }, [submitPending, finalizeTest]);

  // Absolute clock — avoids setInterval drift
  useEffect(() => {
    if (!endsAt || finalized) return undefined;
    const tick = () => {
      const remaining = Math.max(0, Math.floor((endsAt - Date.now()) / 1000));
      setTimeRemaining(remaining);
      if (remaining <= 0 && !submitPending) {
        finalizeTest({ reason: 'timeout' });
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [endsAt, finalized, finalizeTest, submitPending]);

  const urgency = useMemo(() => {
    if (timeRemaining <= 60) return 'critical';
    if (timeRemaining <= 5 * 60) return 'warn';
    return 'ok';
  }, [timeRemaining]);
  const timerColor = urgency === 'critical' ? PINK : urgency === 'warn' ? '#d97706' : ACCENT;

  const handleConfirmSubmit = async () => {
    setSubmitting(true);
    try {
      await finalizeTest({ reason: 'manual' });
    } finally {
      setSubmitting(false);
      setConfirmSubmitDialog(false);
    }
  };

  const handleTimeoutClose = () => {
    setTimeoutDialog(false);
    navigate(`/neet-tests/${testId}/result`);
  };

  const setMcqAnswer = (questionId, selectedOptionIndex) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { ...prev[questionId], selectedOptionIndex, answerText: undefined },
    }));
  };
  const setFillAnswer = (questionId, answerText) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { ...prev[questionId], answerText, selectedOptionIndex: undefined },
    }));
  };
  const clearAnswer = (questionId) => {
    setAnswers((prev) => {
      const existing = prev[questionId];
      if (!existing) return prev;
      if (existing.markedForReview) {
        return { ...prev, [questionId]: { markedForReview: true } };
      }
      const rest = { ...prev };
      delete rest[questionId];
      return rest;
    });
  };
  const toggleMarkForReview = (questionId) => {
    setAnswers((prev) => {
      const existing = prev[questionId] || {};
      return { ...prev, [questionId]: { ...existing, markedForReview: !existing.markedForReview } };
    });
  };

  // Track native fullscreen state (also changes if the user exits via Esc).
  useEffect(() => {
    const onChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // Best-effort auto-fullscreen for direct navigation/refresh into this page
  // (no guaranteed user gesture here, so browsers may silently block it —
  // the header toggle button covers that case). Always exit on unmount.
  useEffect(() => {
    document.documentElement.requestFullscreen?.().catch(() => {});
    return () => {
      if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    };
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.().catch(() => {});
    } else {
      document.documentElement.requestFullscreen?.().catch(() => {});
    }
  };

  // Warn on tab close / refresh / typing a new URL while the test is still in progress.
  useEffect(() => {
    if (finalized) return undefined;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [finalized]);

  // Warn on in-app navigation too (Navbar links etc.) — this app uses a plain
  // BrowserRouter (no data router), so react-router's useBlocker isn't
  // available; intercept anchor clicks in the capture phase instead, before
  // react-router's own Link handler gets a chance to navigate.
  useEffect(() => {
    if (finalized) return undefined;
    const warning =
      "You have a NEET test in progress. The timer keeps running even if you leave — you'll be able to resume, but won't get the time back. Leave anyway?";
    const handleClick = (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const anchor = e.target.closest('a[href]');
      if (!anchor) return;
      let url;
      try {
        url = new URL(anchor.href, window.location.origin);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin || url.pathname === window.location.pathname) return;
      if (!window.confirm(warning)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [finalized]);

  // Warn on browser Back/Forward too — trap it with a duplicate history entry
  // and only actually navigate back if the user confirms.
  useEffect(() => {
    if (finalized) return undefined;
    const warning =
      "You have a NEET test in progress. The timer keeps running even if you leave — you'll be able to resume, but won't get the time back. Leave anyway?";
    window.history.pushState(null, '', window.location.href);
    const handlePopState = () => {
      if (window.confirm(warning)) {
        window.removeEventListener('popstate', handlePopState);
        window.history.back();
      } else {
        window.history.pushState(null, '', window.location.href);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [finalized]);

  // Keep the active subject tab in sync with whichever question Previous/Next lands on.
  useEffect(() => {
    const subject = test?.questions?.[currentIndex]?.subject;
    if (subject) setActiveSubject(subject);
  }, [currentIndex, test]);

  // Drag-to-resize the question-list panel divider.
  useEffect(() => {
    const onMouseMove = (e) => {
      if (!isDraggingRef.current || !bodyRef.current) return;
      const rect = bodyRef.current.getBoundingClientRect();
      const next = Math.min(LEFT_PANEL_MAX, Math.max(LEFT_PANEL_MIN, e.clientX - rect.left));
      setLeftPanelWidth(next);
    };
    const stopDragging = () => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', stopDragging);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', stopDragging);
    };
  }, []);

  const startPanelDrag = (e) => {
    e.preventDefault();
    isDraggingRef.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  if (isLoading) {
    return (
      <Box sx={{ minHeight: '70vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, bgcolor: pageBg }}>
        <CircularProgress sx={{ color: ACCENT }} />
        <Typography sx={{ color: muted, fontWeight: 600 }}>Loading test…</Typography>
      </Box>
    );
  }

  if (!test) return null;

  const questions = test.questions || [];
  const currentQuestion = questions[currentIndex];
  const attemptedCount = questions.filter((q) => isAnswerFilled(answers[q._id])).length;
  const saveMeta = saveStatus ? SAVE_STATUS_META[saveStatus] : null;
  const SaveIcon = saveMeta?.icon;
  const currentAnswer = currentQuestion ? answers[currentQuestion._id] : undefined;
  const currentIsAnswered = isAnswerFilled(currentAnswer);
  const currentIsMarked = Boolean(currentAnswer?.markedForReview);

  const subjects = [];
  questions.forEach((q) => {
    if (q.subject && !subjects.includes(q.subject)) subjects.push(q.subject);
  });

  const visibleQuestions = questions
    .map((q, index) => ({ ...q, _index: index }))
    .filter((q) => !activeSubject || !q.subject || q.subject === activeSubject);

  const jumpToSubject = (subject) => {
    setActiveSubject(subject);
    const firstIndex = questions.findIndex((q) => q.subject === subject);
    if (firstIndex >= 0) setCurrentIndex(firstIndex);
  };

  const renderPaletteButton = (q, index) => {
    const answer = answers[q._id];
    const isAttempted = isAnswerFilled(answer);
    const isMarked = Boolean(answer?.markedForReview);
    const isCurrent = index === currentIndex;
    let bgcolor = 'transparent';
    let color = isDark ? '#c9d1d9' : '#0f2a2b';
    if (isMarked) {
      bgcolor = alpha(REVIEW, isDark ? 0.3 : 0.16);
      color = isDark ? '#c4b5fd' : REVIEW;
    } else if (isAttempted) {
      bgcolor = alpha('#1a7f37', isDark ? 0.25 : 0.14);
      color = '#1a7f37';
    }
    return (
      <Box
        key={q._id}
        component="button"
        type="button"
        onClick={() => setCurrentIndex(index)}
        sx={{
          all: 'unset',
          position: 'relative',
          boxSizing: 'border-box',
          cursor: 'pointer',
          textAlign: 'center',
          flexShrink: 0,
          width: { xs: 38, md: 'auto' },
          height: { xs: 38, md: 'auto' },
          display: { xs: 'grid', md: 'block' },
          placeItems: { xs: 'center', md: 'unset' },
          py: { md: 0.85 },
          borderRadius: 1.25,
          fontWeight: 800,
          fontSize: '0.78rem',
          border: isCurrent ? `2px solid ${ACCENT}` : `1px solid ${border}`,
          bgcolor,
          color,
        }}
      >
        {index + 1}
        {isMarked && isAttempted && (
          <Box
            sx={{
              position: 'absolute',
              top: -3,
              right: -3,
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: '#1a7f37',
              border: `1.5px solid ${surface}`,
            }}
          />
        )}
      </Box>
    );
  };

  const questionPalette = <>{visibleQuestions.map((q) => renderPaletteButton(q, q._index))}</>;

  const subjectTabs = subjects.length > 1 && (
    <Tabs
      value={activeSubject || false}
      onChange={(e, value) => jumpToSubject(value)}
      variant="scrollable"
      scrollButtons="auto"
      sx={{
        minHeight: 38,
        borderBottom: `1px solid ${border}`,
        '& .MuiTab-root': { minHeight: 38, py: 0.75, textTransform: 'none', fontWeight: 700, fontSize: '0.78rem' },
        '& .MuiTabs-indicator': { bgcolor: ACCENT },
      }}
    >
      {subjects.map((subject) => {
        const subjectQuestions = questions.filter((q) => q.subject === subject);
        const subjectAttempted = subjectQuestions.filter((q) => isAnswerFilled(answers[q._id])).length;
        return (
          <Tab
            key={subject}
            value={subject}
            label={`${subject} (${subjectAttempted}/${subjectQuestions.length})`}
          />
        );
      })}
    </Tabs>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: { xs: 'calc(100dvh - 56px)', md: 0 }, bgcolor: pageBg }}>
      <Helmet>
        <title>{test.title ? `${test.title} · Live` : 'NEET Test'} | CrafterHack</title>
      </Helmet>

      {/* Sticky header */}
      <Box sx={{ flexShrink: 0, borderBottom: `1px solid ${border}`, bgcolor: surface, zIndex: 5 }}>
        <Box sx={{ px: { xs: 1.25, sm: 2 }, py: 1.1, display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1.5 } }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: muted, display: { xs: 'none', sm: 'block' } }}>
                NEET mock test
              </Typography>
              {saveMeta && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35 }}>
                  <SaveIcon sx={{ fontSize: 14, color: saveMeta.color }} />
                  <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: saveMeta.color, display: { xs: 'none', sm: 'block' } }}>
                    {saveMeta.label}
                  </Typography>
                </Box>
              )}
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: '0.85rem', sm: '1.05rem' },
                letterSpacing: '-0.02em',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {test.title}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.6, sm: 1 },
              px: { xs: 0.9, sm: 1.5 },
              py: { xs: 0.5, sm: 0.75 },
              borderRadius: 2,
              border: `1px solid ${alpha(timerColor, 0.35)}`,
              bgcolor: alpha(timerColor, isDark ? 0.16 : 0.08),
              minWidth: { xs: 82, sm: 132 },
              flexShrink: 0,
            }}
          >
            <TimerOutlinedIcon sx={{ color: timerColor, fontSize: { xs: 16, sm: 20 } }} />
            <Box>
              <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: timerColor, lineHeight: 1, mb: 0.25, display: { xs: 'none', sm: 'block' } }}>
                Time left
              </Typography>
              <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.82rem', sm: '1.1rem' }, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
                {formatTime(timeRemaining)}
              </Typography>
            </Box>
          </Box>

          <Tooltip title={isFullscreen ? 'Exit full screen' : 'Enter full screen'}>
            <IconButton
              onClick={toggleFullscreen}
              size={isMobile ? 'small' : 'medium'}
              sx={{ flexShrink: 0, border: `1px solid ${border}`, borderRadius: 2, color: muted }}
            >
              {isFullscreen ? <FullscreenExitIcon fontSize={isMobile ? 'small' : 'medium'} /> : <FullscreenIcon fontSize={isMobile ? 'small' : 'medium'} />}
            </IconButton>
          </Tooltip>

          <Button
            variant="contained"
            size={isMobile ? 'small' : 'medium'}
            startIcon={<SendRoundedIcon />}
            onClick={() => setConfirmSubmitDialog(true)}
            disabled={finalized}
            sx={{ flexShrink: 0, textTransform: 'none', fontWeight: 800, borderRadius: 2, bgcolor: ACCENT, boxShadow: 'none', px: { xs: 1.25, sm: 2 }, '&:hover': { bgcolor: '#0f5c5e', boxShadow: 'none' } }}
          >
            Submit
          </Button>
        </Box>
      </Box>

      {/* Body */}
      <Box ref={bodyRef} sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex' }}>
        {!isMobile && (
          <>
            <Box
              sx={{
                width: leftPanelCollapsed ? LEFT_PANEL_COLLAPSED : leftPanelWidth,
                flexShrink: 0,
                minHeight: 0,
                overflow: 'hidden',
                bgcolor: surface,
                borderRight: `1px solid ${border}`,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {leftPanelCollapsed ? (
                <Tooltip title="Expand question list" placement="right">
                  <IconButton size="small" onClick={() => setLeftPanelCollapsed(false)} sx={{ m: 1 }}>
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <>
                  <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.85rem' }}>
                      {attemptedCount} / {questions.length} attempted
                    </Typography>
                    <Tooltip title="Minimize">
                      <IconButton size="small" onClick={() => setLeftPanelCollapsed(true)}>
                        <ChevronLeftIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5, display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 0.75, alignContent: 'flex-start' }}>
                    {questionPalette}
                  </Box>
                </>
              )}
            </Box>

            {!leftPanelCollapsed && (
              <Box
                onMouseDown={startPanelDrag}
                sx={{
                  width: 6,
                  flexShrink: 0,
                  cursor: 'col-resize',
                  position: 'relative',
                  '&:hover::after': { bgcolor: ACCENT },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    left: '50%',
                    top: 0,
                    bottom: 0,
                    width: '2px',
                    bgcolor: border,
                    transform: 'translateX(-50%)',
                  },
                }}
              />
            )}
          </>
        )}

        <Box sx={{ minHeight: 0, flex: 1, overflow: 'auto', p: { xs: 1.5, sm: 3 } }}>
          {subjects.length > 1 && <Box sx={{ mb: 2 }}>{subjectTabs}</Box>}

          {isMobile && (
            <Box
              sx={{
                display: 'flex',
                gap: 0.6,
                mb: 2,
                pb: 0.5,
                overflowX: 'auto',
                overflowY: 'hidden',
                WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
              }}
            >
              {questionPalette}
            </Box>
          )}

          {currentQuestion && (
            <Box sx={{ maxWidth: 720 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                <Chip
                  size="small"
                  label={`Question ${currentIndex + 1} of ${questions.length}`}
                  sx={{ fontWeight: 800, bgcolor: alpha(ACCENT, isDark ? 0.22 : 0.12), color: ACCENT }}
                />
                {currentQuestion.subject && <Chip size="small" variant="outlined" label={currentQuestion.subject} sx={{ fontWeight: 700 }} />}
              </Box>

              <Typography
                sx={{ fontWeight: 700, fontSize: { xs: '0.95rem', sm: '1.05rem' }, lineHeight: 1.5, mb: currentQuestion.image ? 1.5 : 2.5 }}
                dangerouslySetInnerHTML={{ __html: formatScientificText(currentQuestion.questionText) }}
              />

              {currentQuestion.image && (
                <Box
                  component="img"
                  src={currentQuestion.image}
                  alt=""
                  sx={{ display: 'block', maxWidth: '100%', maxHeight: 320, borderRadius: 1.5, border: `1px solid ${border}`, mb: 2.5 }}
                />
              )}

              {currentQuestion.type === 'mcq' ? (
                <RadioGroup
                  value={answers[currentQuestion._id]?.selectedOptionIndex ?? ''}
                  onChange={(e) => setMcqAnswer(currentQuestion._id, Number(e.target.value))}
                >
                  {(currentQuestion.options || []).map((option, i) => {
                    const optionImage = currentQuestion.optionImages?.[i];
                    return (
                      <FormControlLabel
                        key={i}
                        value={i}
                        control={<Radio sx={{ color: ACCENT, '&.Mui-checked': { color: ACCENT }, alignSelf: optionImage ? 'flex-start' : 'center' }} />}
                        label={
                          <Box>
                            {optionImage && (
                              <Box component="img" src={optionImage} alt="" sx={{ display: 'block', maxWidth: '100%', maxHeight: 160, borderRadius: 1, mb: option ? 0.5 : 0 }} />
                            )}
                            {option && <Box component="span" dangerouslySetInnerHTML={{ __html: formatScientificText(option) }} />}
                          </Box>
                        }
                        sx={{
                          mb: 1,
                          mx: 0,
                          px: 1.5,
                          py: 0.75,
                          minHeight: 44,
                          borderRadius: 1.5,
                          border: `1px solid ${border}`,
                          bgcolor: surface,
                          alignItems: optionImage ? 'flex-start' : 'center',
                        }}
                      />
                    );
                  })}
                </RadioGroup>
              ) : (
                <TextField
                  fullWidth
                  placeholder="Type your answer"
                  value={answers[currentQuestion._id]?.answerText ?? ''}
                  onChange={(e) => setFillAnswer(currentQuestion._id, e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: surface } }}
                />
              )}

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 3 }}>
                <Button
                  onClick={() => toggleMarkForReview(currentQuestion._id)}
                  startIcon={currentIsMarked ? <BookmarkIcon /> : <BookmarkBorderIcon />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    minHeight: 40,
                    borderRadius: 1.5,
                    border: `1px solid ${currentIsMarked ? REVIEW : border}`,
                    color: currentIsMarked ? REVIEW : muted,
                    bgcolor: currentIsMarked ? alpha(REVIEW, isDark ? 0.18 : 0.08) : 'transparent',
                  }}
                >
                  {currentIsMarked ? 'Marked for review' : 'Mark for review'}
                </Button>
                <Button
                  onClick={() => clearAnswer(currentQuestion._id)}
                  disabled={!currentIsAnswered}
                  startIcon={<RestartAltIcon />}
                  sx={{ textTransform: 'none', fontWeight: 700, minHeight: 40, borderRadius: 1.5, border: `1px solid ${border}`, color: muted }}
                >
                  Clear response
                </Button>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5, gap: 1 }}>
                <Button
                  disabled={currentIndex === 0}
                  onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
                  sx={{ textTransform: 'none', fontWeight: 700, color: muted, minHeight: 40 }}
                >
                  Previous
                </Button>
                <Button
                  disabled={currentIndex === questions.length - 1}
                  onClick={() => setCurrentIndex((i) => Math.min(questions.length - 1, i + 1))}
                  variant="contained"
                  sx={{ textTransform: 'none', fontWeight: 800, minHeight: 40, bgcolor: ACCENT, boxShadow: 'none', '&:hover': { bgcolor: '#0f5c5e', boxShadow: 'none' } }}
                >
                  Next
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Time up — submitted successfully */}
      <Dialog open={timeoutDialog} onClose={handleTimeoutClose} disableEscapeKeyDown PaperProps={{ sx: { borderRadius: 3, border: `1px solid ${border}`, maxWidth: 420 } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>Time's up</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: muted, lineHeight: 1.6 }}>
            Your test time has expired. Your answers were submitted automatically — you can review your result now.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleTimeoutClose} variant="contained" sx={{ textTransform: 'none', fontWeight: 800, bgcolor: ACCENT, '&:hover': { bgcolor: '#0f5c5e' } }}>
            View result
          </Button>
        </DialogActions>
      </Dialog>

      {/* Time up — couldn't reach the server yet, retrying */}
      <Dialog open={submitPending} disableEscapeKeyDown PaperProps={{ sx: { borderRadius: 3, border: `1px solid ${border}`, maxWidth: 420 } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>Time's up — reconnecting</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: muted, lineHeight: 1.6 }}>
            Your test time has expired and your answers are safely saved. We couldn't reach the server to submit —
            we'll keep retrying automatically once you're back online.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => finalizeTest({ reason: 'timeout' })}
            variant="contained"
            startIcon={<CircularProgress size={16} color="inherit" />}
            sx={{ textTransform: 'none', fontWeight: 800, bgcolor: ACCENT, '&:hover': { bgcolor: '#0f5c5e' } }}
          >
            Retry now
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm submit */}
      <Dialog open={confirmSubmitDialog} onClose={() => !submitting && setConfirmSubmitDialog(false)} PaperProps={{ sx: { borderRadius: 3, border: `1px solid ${border}`, maxWidth: 420 } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>Submit test?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: muted, lineHeight: 1.6 }}>
            You've attempted {attemptedCount} of {questions.length} questions. Once submitted, you can't change your
            answers. Negative marking applies to incorrect attempts.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setConfirmSubmitDialog(false)} disabled={submitting} sx={{ textTransform: 'none', fontWeight: 700, color: muted }}>
            Keep going
          </Button>
          <Button
            onClick={handleConfirmSubmit}
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutlineIcon />}
            sx={{ textTransform: 'none', fontWeight: 800, bgcolor: ACCENT, '&:hover': { bgcolor: '#0f5c5e' } }}
          >
            {submitting ? 'Submitting…' : 'Submit now'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NeetTestLive;
